import { ProductKey } from '@/modules/estate/enums/ProductKey';
import { ColumnFormat } from '@/modules/core/app/constants/data.constants';
import { HubSpotRecurringBillingFrequency } from '@/modules/hubspot/constants/HubSpotRecurringBillingFrequency';
import { BillingPlanId } from '@/modules/billing/constants/BillingPlanId';
import { BillingPlanPrice } from '@/modules/billing/models/BillingPlanPrice';
import { BillingPlanIdMapper } from '@/modules/billing/mappers/BillingPlanIdMapper';
import BillingService from '/@/modules/billing/services/BillingService';
import Lang from '@/modules/core/app/services/Lang';
import NumeralService from '@/modules/core/app/services/NumeralService';

export class FinancialAdvisorProBillingPlanForm {
  name?: string;

  description?: string;

  price?: number;

  note?: string;

  frequency?: HubSpotRecurringBillingFrequency;

  inclusions?: string[];

  savePercentage?: number;

  productKey?: ProductKey;

  constructor(model: Partial<FinancialAdvisorProBillingPlanForm> = {}) {
    this.name = model.name;
    this.description = model.description;
    this.price = model.price;
    this.note = model.note;
    this.frequency = model.frequency;
    this.inclusions = model.inclusions ?? [];
    this.savePercentage = model.savePercentage;
    this.productKey = model.productKey;
  }

  get isYearly(): boolean {
    return this.frequency == HubSpotRecurringBillingFrequency.ANNUALLY;
  }

  get priceSubText(): string {
    return this.isYearly
      ? Lang.t('financialadvisor.billing-annual-plan-subtext')
      : Lang.t('financialadvisor.billing-monthly-plan-subtext');
  }

  get formattedPrice(): string {
    if (!this.price) {
      return '-';
    }

    return NumeralService.formatValue(this.price, ColumnFormat.FORMAT_CURRENCY, 0);
  }

  async loadPlanPrice(): Promise<void> {
    const billingPlanId: BillingPlanId | null = BillingPlanIdMapper.getBillingPlanId(
      this.productKey as ProductKey
    );
    const billingPlanPrice: BillingPlanPrice = await BillingService.calculateBillingPlanPrices({
      billingPlanId
    });

    this.price = billingPlanPrice?.price;
  }

  setAsAnnualPlan(): this {
    this.name = Lang.t('financialadvisor.billing-annual-plan-name');
    this.description = Lang.t('financialadvisor.billing-annual-plan-description');
    this.frequency = HubSpotRecurringBillingFrequency.ANNUALLY;
    this.note = Lang.t('financialadvisor.billing-plan-note');
    this.inclusions = [
      Lang.t('financialadvisor.billing-annual-plan-inclusion-1'),
      Lang.t('financialadvisor.billing-plan-inclusion-2'),
      Lang.t('financialadvisor.billing-plan-inclusion-3'),
      Lang.t('financialadvisor.billing-plan-annual-plan-inclusion-2')
    ];
    this.savePercentage = 16;
    this.productKey = ProductKey.ADVISOR_PRO_ANNUAL;

    return this;
  }

  setAsMonthlyPlan(): this {
    this.name = Lang.t('financialadvisor.billing-monthly-plan');
    this.description = Lang.t('financialadvisor.billing-monthly-plan-description');
    this.note = Lang.t('financialadvisor.billing-plan-note');
    this.frequency = HubSpotRecurringBillingFrequency.MONTHLY;
    this.inclusions = [
      Lang.t('financialadvisor.billing-monthly-plan-inclusion-1'),
      Lang.t('financialadvisor.billing-plan-inclusion-2'),
      Lang.t('financialadvisor.billing-plan-inclusion-3')
    ];
    this.productKey = ProductKey.ADVISOR_PRO;

    return this;
  }
}
